import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, ShieldCheck, Heart, Eye, Tag, Send, UserRound, Flame, Clock, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, priceLabel, timeAgo, formatErr } from "@/lib/api";
import { catName, condName, eraName, SAFETY_TIPS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Stars } from "@/components/Stars";
import { RateDialog, useRatingState } from "@/components/RateDialog";
import { ReportDialog } from "@/components/ReportDialog";
import { ShareButtons } from "@/components/ShareButtons";
import { SoldSticker } from "@/components/SoldSticker";
import { SoldBadge } from "@/components/SoldBadge";
import { OfferDialog } from "@/components/OfferDialog";
import { SimilarItems } from "@/components/SimilarItems";
import { FastReplierBadge } from "@/components/TrustBadges";

const QUICK = ["Hi! Is this still available?", "Can I pick up today?", "Would you take an offer?"];

function ContactBox({ listing }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [text, setText] = useState(QUICK[0]);
  const [busy, setBusy] = useState(false);
  const mine = user && user.id === listing.seller_id;

  const send = async () => {
    if (!user) return nav(`/login?next=/listing/${listing.id}`);
    setBusy(true);
    try {
      const { data } = await api.post("/conversations", { listing_id: listing.id, text });
      toast.success("Message sent to seller");
      nav(`/messages/${data.id}`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  if (mine) return <Link to={`/edit/${listing.id}`} data-testid="detail-edit-btn" className="btn-lime w-full py-3.5 text-sm">Edit my listing</Link>;
  if (["hidden", "removed"].includes(listing.status)) return <div data-testid="detail-review-banner" className="rounded-xl bg-orange-500/15 p-4 text-center text-sm font-bold text-orange-300">This listing is under review by the ISoldIt team</div>;
  if (listing.status === "sold") return <div data-testid="detail-sold-banner" className="rounded-xl bg-[#d4f253]/15 p-4 text-center font-bold text-[#d4f253]">ISoldIt! This item has been sold — <Link to="/browse" className="underline">find similar items</Link></div>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {QUICK.map((q, i) => (
          <button key={q} data-testid={`detail-quick-reply-${i}`} onClick={() => setText(q)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${text === q ? "bg-[#d4f253] text-[#0f2a33]" : "bg-white/10 hover:bg-white/15"}`}>{q}</button>
        ))}
      </div>
      <textarea data-testid="detail-message-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} className="field resize-none text-sm" />
      <button data-testid="detail-message-seller-btn" disabled={busy || !text.trim()} onClick={send} className="btn-lime w-full py-3.5 text-sm">
        <Send className="h-4 w-4" /> {user ? "Message seller" : "Log in to message seller"}
      </button>
      {listing.status === "active" && !listing.is_free && listing.price > 0 && <OfferDialog listing={listing} />}
    </div>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [l, setL] = useState(null);
  const [err, setErr] = useState(false);
  const [active, setActive] = useState(0);
  const [saved, setSaved] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [myRating, reloadMine] = useRatingState(id, user);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setL(r.data)).catch(() => setErr(true));
    let vid = localStorage.getItem("isoldit_visitor");
    if (!vid) { vid = crypto.randomUUID?.() || String(Math.random()).slice(2); localStorage.setItem("isoldit_visitor", vid); }
    api.post(`/listings/${id}/view`, null, { headers: { "X-Visitor-Id": vid } }).catch(() => {});
  }, [id]);
  useEffect(() => {
    if (l?.seller_id) api.get(`/users/${l.seller_id}/ratings`).then((r) => setReviews(r.data.items));
  }, [l?.seller_id]);
  const refreshRatings = () => {
    reloadMine();
    api.get(`/listings/${id}`).then((r) => setL(r.data));
    api.get(`/users/${l.seller_id}/ratings`).then((r) => setReviews(r.data.items));
  };
  useEffect(() => {
    if (user) api.get("/my/saved-ids").then((r) => setSaved(r.data.includes(id))).catch(() => {});
  }, [user, id]);

  const toggleSave = async () => {
    if (!user) return toast("Log in to save items");
    const { data } = await api.post(`/listings/${id}/save`);
    setSaved(data.saved);
    toast.success(data.saved ? "Saved to your list" : "Removed from saved");
  };

  if (err) return <div data-testid="detail-not-found" className="py-32 text-center text-slate-400">Listing not found.</div>;
  if (!l) return <div className="py-32 text-center text-slate-400">Loading…</div>;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-3">
          <div className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-[#173742]">
            {l.images?.[active] && <img data-testid="detail-main-image" src={imgUrl(l.images[active])} alt={l.title} className={`h-full w-full object-cover ${l.status === "sold" ? "grayscale-[60%]" : ""}`} />}
            {l.status === "sold" && <SoldSticker size="lg" testId="detail-sold-sticker" />}
            {l.popular && <span data-testid="detail-popular-badge" className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-orange-500 px-3 py-1.5 text-xs font-extrabold uppercase text-white shadow"><Flame className="h-4 w-4" />Popular · {l.weekly_views} views this week</span>}
            {!["active", "sold"].includes(l.status) && <span className="absolute left-4 top-4 rounded-full bg-orange-500 px-4 py-1.5 text-sm font-bold uppercase">{l.status}</span>}
          </div>
          {l.images?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {l.images.map((im, i) => (
                <button key={im} data-testid={`detail-thumb-${i}`} onClick={() => setActive(i)} className={`h-20 w-24 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${i === active ? "border-[#d4f253]" : "border-transparent opacity-70"}`}>
                  <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="rounded-3xl bg-[#173742] p-6 sm:p-8">
            <h2 className="font-display text-lg font-bold">Description</h2>
            <p data-testid="detail-description" className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-300">{l.description || "No description provided."}</p>
          </div>
        </div>

        <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 text-xs font-semibold">
              <span className="rounded-full bg-white/10 px-3 py-1">{catName(l.category)}</span>
              <span className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1"><Tag className="h-3 w-3" />{condName(l.condition)}</span>
              {l.era && <span data-testid="detail-era" className="flex items-center gap-1 rounded-full bg-[#d4f253]/15 px-3 py-1 text-[#d4f253]"><Hourglass className="h-3 w-3" />{eraName(l.era)}</span>}
              {l.maker && <span data-testid="detail-maker" className="rounded-full bg-[#d4f253]/15 px-3 py-1 text-[#d4f253]">Maker: {l.maker}</span>}
            </div>
            <h1 data-testid="detail-title" className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{l.title}</h1>
            <p data-testid="detail-price" className="font-display text-4xl font-extrabold tracking-tight text-[#d4f253]">{priceLabel(l)}</p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{l.suburb} {l.postcode}, {l.state}</span>
              <span className="flex items-center gap-1"><Eye className="h-4 w-4" />{l.views} views</span>
              <span>{timeAgo(l.created_at)}</span>
            </div>
          </div>

          <div data-testid="detail-pay-on-pickup-badge" className="flex gap-3 rounded-2xl border border-[#d4f253]/40 bg-[#d4f253]/10 p-4">
            <ShieldCheck className="h-6 w-6 shrink-0 text-[#d4f253]" />
            <div className="text-sm">
              <p className="font-bold text-[#d4f253]">Pay on pick-up</p>
              <p className="text-slate-300">Inspect before paying cash or PayID directly to the seller. Never pay upfront.</p>
            </div>
          </div>

          <div className="rounded-2xl bg-[#173742] p-5">
            <ContactBox listing={l} />
            <button data-testid="detail-save-btn" onClick={toggleSave} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 py-3 text-sm font-semibold transition-colors hover:bg-white/5">
              <Heart className={`h-4 w-4 ${saved ? "fill-[#d4f253] text-[#d4f253]" : ""}`} /> {saved ? "Saved" : "Save item"}
            </button>
          </div>

          {l.status === "active" && (
            <div data-testid="detail-share-section" className="space-y-3 rounded-2xl border border-white/10 p-5">
              <p className="text-sm font-bold">{user?.id === l.seller_id ? "Share your listing to sell it faster" : "Share this item"}</p>
              <ShareButtons url={`${window.location.origin}/api/share/listing/${l.id}`} text={`${l.title} — ${l.is_free ? "Free" : `$${l.price}`} on ISoldIt:`} />
            </div>
          )}

          {l.seller && (
            <div data-testid="detail-seller-card" className="space-y-4 rounded-2xl bg-[#173742] p-5">
              <div className="flex items-center gap-4">
                <span className="grid h-12 w-12 place-items-center rounded-full bg-[#d4f253] text-[#0f2a33]"><UserRound className="h-6 w-6" /></span>
                <div className="text-sm">
                  <Link to={`/seller/${l.seller.id}`} data-testid="detail-seller-profile-link" className="font-bold hover:text-[#d4f253] hover:underline">{l.seller.name}</Link>
                  <div className="my-1 flex flex-wrap gap-1.5"><SoldBadge count={l.seller.sold_count} testId="detail-seller-sold-badge" />{l.seller.response?.fast && <FastReplierBadge testId="detail-seller-fast-badge" />}</div>
                  <p className="flex items-center gap-1.5 text-slate-300" data-testid="detail-seller-rating">
                    {l.seller.rating?.count ? <><Stars value={l.seller.rating.avg} />{l.seller.rating.avg} ({l.seller.rating.count})</> : <span className="text-slate-400">No ratings yet</span>}
                  </p>
                  <p className="text-slate-400">{l.seller.active_listings} active listings · member since {new Date(l.seller.member_since).getFullYear()}</p>
                  {l.seller.response?.label && <p data-testid="detail-seller-response" className="mt-0.5 flex items-center gap-1 text-xs text-[#d4f253]"><Clock className="h-3.5 w-3.5" />Usually replies {l.seller.response.label}</p>}
                </div>
              </div>
              {myRating.eligible && (
                <button data-testid="detail-rate-seller-btn" onClick={() => setRateOpen(true)} className="w-full rounded-xl border border-[#d4f253]/40 py-2.5 text-sm font-semibold text-[#d4f253] transition-colors hover:bg-[#d4f253]/10">
                  {myRating.rating ? `You rated ${myRating.rating.stars}★ — edit` : "Picked it up? Rate this seller"}
                </button>
              )}
              {reviews.length > 0 && (
                <ul data-testid="detail-reviews" className="space-y-2 border-t border-white/10 pt-4">
                  {reviews.slice(0, 3).map((r) => (
                    <li key={r.id} className="text-xs">
                      <div className="flex items-center gap-2"><Stars value={r.stars} size="h-3 w-3" /><span className="font-semibold">{r.buyer_name}</span><span className="text-slate-500">{timeAgo(r.created_at)}</span></div>
                      {r.comment && <p className="mt-1 text-slate-300">{r.comment}</p>}
                    </li>
                  ))}
                </ul>
              )}
              <RateDialog open={rateOpen} onOpenChange={setRateOpen} listingId={l.id} sellerName={l.seller.name} existing={myRating.rating} onDone={refreshRatings} />
            </div>
          )}

          <div className="rounded-2xl border border-white/10 p-5">
            <p className="mb-3 text-sm font-bold">Stay safe</p>
            <ul className="space-y-2 text-xs text-slate-400">{SAFETY_TIPS.slice(0, 3).map((t) => <li key={t}>• {t}</li>)}</ul>
            {user?.id !== l.seller_id && <div className="mt-4"><ReportDialog listingId={l.id} user={user} onNeedLogin={() => nav(`/login?next=/listing/${l.id}`)} /></div>}
          </div>
        </aside>
      </div>
      {l.status === "sold" && <SimilarItems listing={l} />}
    </div>
  );
}
